import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Check, Cross, Info, Warning } from "./Icons.js";

export type ToastKind = "info" | "success" | "error";

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  show: (message: string, kind?: ToastKind) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Wie lange ein Toast stehen bleibt, bevor er sich selbst entfernt. */
const AUTO_DISMISS_MS: Record<ToastKind, number> = {
  info: 5000,
  success: 5000,
  // Fehler bleiben laenger stehen -- sie enthalten meist etwas, das der Nutzer
  // lesen und verstehen muss.
  error: 10_000,
};

export function ToastProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (message: string, kind: ToastKind = "info") => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, kind, message }]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), AUTO_DISMISS_MS[kind]),
      );
    },
    [dismiss],
  );

  // Alle offenen Timer beim Aushaengen abraeumen.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        Zwei getrennte Live-Regionen: Fehler werden mit `assertive` sofort
        angesagt, alles andere hoeflich mit `polite`. Eine gemeinsame Region
        koennte nur eine der beiden Dringlichkeiten haben.
      */}
      <div className="toast-region">
        <div aria-live="polite" aria-atomic="false" style={{ display: "contents" }}>
          {toasts
            .filter((toast) => toast.kind !== "error")
            .map((toast) => (
              <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
            ))}
        </div>
        <div aria-live="assertive" aria-atomic="false" style={{ display: "contents" }}>
          {toasts
            .filter((toast) => toast.kind === "error")
            .map((toast) => (
              <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
            ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: number) => void;
}): React.ReactElement {
  const Icon = toast.kind === "success" ? Check : toast.kind === "error" ? Warning : Info;
  // Der Praefix macht die Art der Meldung auch im Vorlesetext eindeutig --
  // das Symbol allein wird nicht vorgelesen.
  const prefix =
    toast.kind === "success" ? "Erfolg: " : toast.kind === "error" ? "Fehler: " : "Hinweis: ";

  return (
    <div className={`toast toast--${toast.kind}`}>
      <span className="toast__icon">
        <Icon size={18} />
      </span>
      <span className="toast__body">
        <span className="visually-hidden">{prefix}</span>
        {toast.message}
      </span>
      <button
        type="button"
        className="toast__close"
        aria-label="Meldung schließen"
        onClick={() => onDismiss(toast.id)}
      >
        <Cross size={16} />
      </button>
    </div>
  );
}

/** Zugriff auf die Toast-Anzeige. Wirft, wenn kein Provider darueber liegt. */
export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast benötigt einen ToastProvider im Baum.");
  return context;
}
